import { css, cx } from "@emotion/css";
import { ArrowUpRightFromSquareIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { resolvePathWithSpaceId } from "@octopusdeploy/portal-routes";
import { forwardRef } from "react";
import type * as React from "react";
import { useParams, Link } from "react-router-dom";
import { client } from "~/clientInstance";
import { isUrl } from "~/components/Navigation/InternalLink/isUrl";
import { useUrlResolver } from "~/components/Navigation/useUrlResolver";
export type FontWeight = "normal" | "bold" | "bolder" | "lighter" | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
interface InternalLinkProps {
    id?: string;
    size?: number;
    weight?: FontWeight;
    width?: string;
    color?: string;
    className?: string;
    to: LinkHref;
    openInSelf?: boolean;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    children?: React.ReactNode;
    accessibleName?: string;
    isDisabled?: boolean;
    showOpensInNewTabIcon?: boolean;
    title?: string;
}
const InternalLink = forwardRef<HTMLAnchorElement, InternalLinkProps>(({ id, size, weight, width, color, className, to, openInSelf = true, onClick, children, accessibleName, isDisabled, showOpensInNewTabIcon = false, title }, ref) => {
    const params = useParams<{
        spaceId?: string;
    }>();
    const urlResolver = useUrlResolver();
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled)
            return;
        onClick?.(e);
        e.stopPropagation();
    };
    const style: React.CSSProperties = {};
    if (size) {
        style.fontSize = size + "rem";
    }
    if (weight) {
        style.fontWeight = weight;
    }
    if (width) {
        style.width = width;
    }
    if (color) {
        style.color = color;
    }
    if (isDisabled) {
        style.color = themeTokens.color.text.disabled;
        style.pointerEvents = "none";
    }
    if (showOpensInNewTabIcon && !openInSelf) {
        style.display = "inline-flex";
    }
    // The client's spaceId and the route's spaceId should be in sync,
    // but because we haven't modelled this relationship in a reliable way,
    // this is not guaranteed to be true, particularly in tests
    const spaceId = (params.spaceId || client.spaceId) ?? undefined;
    const resolvedPath = isUrl(to) ? urlResolver.resolve(to) : resolvePathWithSpaceId(to, spaceId);
    /* eslint-disable react/forbid-elements */
    return (<Link id={id} aria-label={accessibleName} aria-disabled={isDisabled} className={cx(linkStyles, className)} style={style} to={resolvedPath} target={openInSelf ? "_self" : "_blank"} onClick={handleClick} ref={ref} title={title}>
            {children}
            {showOpensInNewTabIcon && !openInSelf && (<span className={iconStyles}>
                    <ArrowUpRightFromSquareIcon size={16}/>
                </span>)}
        </Link>);
});
export default InternalLink;
const linkStyles = css({
    color: themeTokens.color.text.link.default,
    textDecoration: "none",
    "&:hover": {
        color: themeTokens.color.portal.link.text.hover,
    },
});
const iconStyles = css({
    marginLeft: space[4],
    alignContent: "center",
});
